import React, { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { set } from "date-fns";

const BookForm: React.FC = () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { token } = useAppSelector((state) => state.auth);

    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [author, setAuthor] = useState("");
    const [price, setPrice] = useState<number>(0);
    const [category, setCategory] = useState("");
    const [description, setDescription] = useState("");
    const [language, setLanguage] = useState("");
    const [pages, setPages] = useState<number>(0);
    const [publisher, setPublisher] = useState("");
    const [publishedYear, setPublishedYear] = useState("");
    const [stock, setStock] = useState<number>(0);

    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState("");

    const [loading, setLoading] = useState(false);

    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    // 📌 Fetch Book for Edit
    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    useEffect(() => {
        if (!id || !token) return;

        const fetchBook = async () => {
            try {
                const res = await fetch(`${baseUrl}/books/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const data = await res.json();

                console.log("Fetched book response data:", data);
                if (data?.success && data?.data) {
                    const b = data.data;

                    setTitle(b.title || "");
                    setSlug(b.slug || "");
                    setAuthor(b.author || "");
                    setPrice(b.price || 0);
                    setCategory(b.category || "");
                    setDescription(b.description || "");
                    setLanguage(b.language || "");
                    setPages(b.pages || 0);
                    setPublisher(b.publisher || "");
                    setPublishedYear(b.publishedYear || "");
                    setStock(b.stock || 0);

                    if (b.image) {
                        setPreviewUrl(b.image.startsWith("http") ? b.image : `${baseUrl}/${b.image}`);
                    }
                }
            } catch (err) {
                console.error("❌ Error fetching book:", err);
                alert("Failed to load book");
            }
        };

        fetchBook();
    }, [id, token, baseUrl]);

    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    // 📌 Preview Image
    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    useEffect(() => {
        if (!image) return;

        const url = URL.createObjectURL(image);
        setPreviewUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [image]);

    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    // 📌 Submit Form (Add / Edit)
    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!id && !image) {
            alert("Please upload book cover image!");
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("slug", slug);
            formData.append("author", author);
            formData.append("price", price.toString());
            formData.append("category", category);
            formData.append("description", description);
            formData.append("language", language);
            formData.append("pages", pages.toString());
            formData.append("publisher", publisher);
            formData.append("publishedYear", publishedYear.toString());
            formData.append("stock", stock.toString());

            if (image) formData.append("image", image);

            const url = id
                ? `${baseUrl}/books/${id}`
                : `${baseUrl}/books`;

            const method = id ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                body: formData,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await res.json();

            console.log("Response data:", data);

            if (res.status) {
                alert(id ? "✅ Book updated!" : "✅ Book added!");
                navigate("/books");
            } else {
                alert(data?.message || "Something went wrong!");
            }
        } catch (err) {
            console.error("❌ Error submitting book:", err);
            alert("Failed to submit book");
        } finally {
            setLoading(false);
        }
    };

    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    // 📌 UI
    // ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
    return (
        <div className="main-container container-fluid">
            <div className="page-header mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/dashboard">Home</a></li>
                    <li className="breadcrumb-item"><a href="/books">Books</a></li>
                    <li className="breadcrumb-item active">{id ? "Edit Book" : "Add Book"}</li>
                </ol>
                <h1 className="page-title">{id ? "Edit Book" : "Add Book"}</h1>
            </div>

            <form onSubmit={handleSubmit} className="card p-4" encType="multipart/form-data">

                <div className="form-group mb-3">
                    <label>Title</label>
                    <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} required />
                </div>

                <div className="form-group mb-3">
                    <label>Slug</label>
                    <input type="text" className="form-control" value={slug} onChange={e => setSlug(e.target.value)} required />
                </div>

                <div className="form-group mb-3">
                    <label>Author</label>
                    <input type="text" className="form-control" value={author} onChange={e => setAuthor(e.target.value)} required />
                </div>

                <div className="form-group mb-3">
                    <label>Price</label>
                    <input type="number" className="form-control" value={price} onChange={e => setPrice(Number(e.target.value))} required />
                </div>

                <div className="form-group mb-3">
                    <label>Category</label>
                    <input type="text" className="form-control" value={category} onChange={e => setCategory(e.target.value)} required />
                </div>

                <div className="form-group mb-3">
                    <label>Description</label>
                    <textarea className="form-control" rows={5} value={description} onChange={e => setDescription(e.target.value)} required />
                </div>

                <div className="form-group mb-3">
                    <label>Language</label>
                    <input type="text" className="form-control" value={language} onChange={e => setLanguage(e.target.value)} required />
                </div>

                <div className="form-group mb-3">
                    <label>Pages</label>
                    <input type="number" className="form-control" value={pages} onChange={e => setPages(Number(e.target.value))} required />
                </div>

                <div className="form-group mb-3">
                    <label>Publisher</label>
                    <input type="text" className="form-control" value={publisher} onChange={e => setPublisher(e.target.value)} />
                </div>

                <div className="form-group mb-3">
                    <label>Published Year</label>
                    <input type="number" className="form-control" value={publishedYear} onChange={e => setPublishedYear(e.target.value)} />
                </div>

                <div className="form-group mb-3">
                    <label>Stock</label>
                    <input type="number" className="form-control" value={stock} onChange={e => setStock(Number(e.target.value))} />
                </div>

                {/* IMAGE UPLOAD */}
                <div
                    style={{ border: "2px dashed #bbb", padding: "30px", textAlign: "center", borderRadius: "12px", cursor: "pointer" }}
                    onClick={() => document.getElementById("fileInputBook")?.click()}
                >
                    {previewUrl ? (
                        <div>
                            <img src={previewUrl} alt="Preview" style={{ maxHeight: "180px", borderRadius: "8px" }} />
                            <button type="button"
                                className="btn btn-sm btn-danger mt-3"
                                onClick={(e) => { e.stopPropagation(); setImage(null); setPreviewUrl(""); }}
                            >
                                Remove
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className="fw-bold mb-1">Upload Book Cover</p>
                            <p className="text-muted mb-0">Click to browse</p>
                        </>
                    )}

                    <input
                        id="fileInputBook"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => e.target.files && setImage(e.target.files[0])}
                        required={!id}
                    />
                </div>

                <button type="submit" className="btn btn-primary w-100 mt-4" disabled={loading}>
                    {loading ? (id ? "Updating..." : "Creating...") : id ? "Update Book" : "Add Book"}
                </button>
            </form>
        </div>
    );
};

export default BookForm;
